/* KoreCare behaviour — scroll reveal, video playback, 3D stage.
   One file, no dependencies. Everything degrades to static if JS fails. */
(() => {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Opt in to the reveal animation only once JS is confirmed running, so a
  // script failure leaves everything visible rather than blank.
  document.documentElement.classList.add('kc-js');

  /* --- reveal on scroll ------------------------------------------------ */
  const revealables = document.querySelectorAll('.kc-reveal');
  if (revealables.length) {
    if (reduced) {
      revealables.forEach((el) => el.classList.add('is-in'));
    } else {
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (!e.isIntersecting) return;
            e.target.classList.add('is-in');
            io.unobserve(e.target); // one-shot; no thrash on scroll-back
          });
        },
        { rootMargin: '0px 0px -12% 0px', threshold: 0.15 }
      );
      revealables.forEach((el) => io.observe(el));
    }
  }

  /* --- video: always playing wherever you can see it ------------------- */
  // Muted autoplay, started half a screen before a video scrolls in, and the
  // half-visible next reel in a rail plays too (threshold 0) — so nobody ever
  // sees a paused one. Only off-screen videos pause: twenty-odd streams at
  // once would eat a phone's data and its hardware decoders.
  const vio = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        const v = e.target;
        if (e.isIntersecting) {
          // play() rejects if the browser blocks all autoplay (iPhone Low
          // Power Mode) — the poster stays up and a tap still starts it.
          v.play().catch(() => {});
        } else {
          v.pause();
        }
      });
    },
    { rootMargin: '50% 0px', threshold: 0 }
  );
  const watchVideos = (root) => root.querySelectorAll('video[data-kc-video]').forEach((v) => vio.observe(v));
  watchVideos(document);

  /* --- hero film: park the loop once it scrolls away ------------------- */
  const film = document.querySelector('.kc-hero__film');
  if (film) {
    new IntersectionObserver(
      ([e]) => film.classList.toggle('is-paused', !e.isIntersecting),
      { threshold: 0 }
    ).observe(film);
  }

  /* --- mute toggles ---------------------------------------------------- */
  const setMuted = (v, muted) => {
    v.muted = muted;
    const btn = (v.closest('[data-kc-videowrap]') || document).querySelector('[data-kc-mute]');
    if (!btn) return;
    btn.setAttribute('aria-pressed', String(!muted));
    btn.querySelector('[data-kc-mute-on]')?.toggleAttribute('hidden', muted);
    btn.querySelector('[data-kc-mute-off]')?.toggleAttribute('hidden', !muted);
  };
  document.addEventListener('click', (e) => {
    // A tap on a reel itself works like its speaker button — the reels habit.
    const btn =
      e.target.closest('[data-kc-mute]') ||
      (e.target.matches('.kc-vid__cell video') && e.target.parentElement.querySelector('[data-kc-mute]'));
    if (!btn) return;
    const scope = btn.closest('[data-kc-videowrap]') || document;
    const v = scope.querySelector('video[data-kc-video]');
    if (!v) return;
    // One voice at a time: a rail can have four reels on screen at once.
    if (v.muted) document.querySelectorAll('video[data-kc-video]').forEach((o) => o !== v && !o.muted && setMuted(o, true));
    setMuted(v, !v.muted);
    // Low Power Mode and data savers block autoplay; a tap is allowed to start it.
    if (v.paused) v.play().catch(() => {});
  });

  /* --- 3D scenes: one range input drives one --p per stage ------------- */
  document.querySelectorAll('[data-kc-scene]').forEach((scene) => {
    const stage = scene.querySelector('.kc-scene__stage');
    const range = scene.querySelector('[data-kc-range]');
    const state = scene.querySelector('[data-kc-state]');
    const count = scene.querySelector('[data-kc-count]');
    // "from-to": the stretch of the slider the ten minutes run over.
    const [c0, c1] = (count?.dataset.kcCount || '0-1').split('-').map(Number);
    const steps = (scene.querySelector('[data-kc-steps]')?.dataset.kcSteps || '').split('|');
    if (!stage || !range) return;

    const apply = (v) => {
      const p = v / 100;
      stage.style.setProperty('--p', p);
      if (state && steps.length) {
        // Thirds map cleanly onto the three-stage labels every scene uses.
        const i = Math.min(steps.length - 1, Math.floor(p * steps.length));
        state.textContent = steps[i];
      }
      if (count) {
        const secs = Math.round(Math.min(1, Math.max(0, (p - c0) / (c1 - c0))) * 600); // the mask's ten minutes
        count.textContent = `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;
      }
    };

    range.addEventListener('input', () => apply(+range.value));
    apply(+range.value);

    if (reduced) return;

    // Mouse-only tilt for stages that opt in. Touch is left alone: a drag on
    // the stage there is the page scrolling, not a request to turn the scene.
    if (stage.hasAttribute('data-kc-tilt')) {
      stage.addEventListener('pointermove', (e) => {
        if (e.pointerType !== 'mouse') return;
        const r = stage.getBoundingClientRect();
        stage.style.setProperty('--tx', `${((e.clientX - r.left) / r.width - 0.5) * 30}deg`);
        stage.style.setProperty('--ty', `${((e.clientY - r.top) / r.height - 0.5) * -20}deg`);
      });
      stage.addEventListener('pointerleave', () => {
        stage.style.removeProperty('--tx');
        stage.style.removeProperty('--ty');
      });
    }

    // One-shot intro so the scene reads as interactive before it is touched.
    let played = false;
    new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((e) => {
          if (!e.isIntersecting || played) return;
          played = true;
          obs.disconnect();
          const start = performance.now();
          const tick = (now) => {
            const t = Math.min(1, (now - start) / 1400);
            const eased = 1 - Math.pow(1 - t, 3);
            range.value = String(Math.round(eased * 45));
            apply(+range.value);
            if (t < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        });
      },
      { threshold: 0.4 }
    ).observe(scene);
  });

  /* --- jump nav: mark where you are, and keep the rail honest ---------- */
  const rail = document.querySelector('.kc-anchors__inner');
  if (rail) {
    const links = [...rail.querySelectorAll('a')];

    const bar = rail.parentElement;

    // A gradient is a hint, not a control. These are real buttons: they say
    // "there is more this way" unambiguously, and they give a target to tap for
    // anyone who will not think to swipe a 40px-tall strip of text.
    const arrow = (dir) => {
      const el = document.createElement('button');
      el.type = 'button';
      el.className = 'kc-anchors__arrow kc-anchors__arrow--' + dir;
      el.setAttribute('aria-label', dir === 'next' ? 'Show later sections' : 'Show earlier sections');
      el.innerHTML =
        '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" ' +
        'stroke-width="2.2" stroke-linecap="square" aria-hidden="true"><path d="' +
        (dir === 'next' ? 'M9 5l7 7-7 7' : 'M15 5l-7 7 7 7') +
        '"/></svg>';
      el.addEventListener('click', () => {
        rail.scrollBy({
          left: (dir === 'next' ? 1 : -1) * rail.clientWidth * 0.7,
          behavior: reduced ? 'auto' : 'smooth',
        });
      });
      return el;
    };
    bar.append(arrow('prev'), arrow('next'));

    // Classes go on the bar, not the rail, so the arrows and the fades can both
    // key off them. `fits` means there is nothing to scroll to at all.
    const edges = () => {
      const max = rail.scrollWidth - rail.clientWidth;
      bar.classList.toggle('fits', max <= 4);
      bar.classList.toggle('at-start', rail.scrollLeft <= 2);
      bar.classList.toggle('at-end', rail.scrollLeft >= max - 2);
    };
    edges();
    rail.addEventListener('scroll', edges, { passive: true });
    addEventListener('resize', edges);

    const targets = links
      .map((a) => document.querySelector(a.getAttribute('href')))
      .filter(Boolean);

    if (targets.length) {
      const spy = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (!e.isIntersecting) return;
            const link = links.find((a) => a.getAttribute('href') === '#' + e.target.id);
            if (!link || link.classList.contains('is-active')) return;
            links.forEach((a) => a.classList.remove('is-active'));
            link.classList.add('is-active');

            // Centre it by writing scrollLeft directly. scrollIntoView would be
            // shorter but it can also move the page vertically, and the whole
            // point of this nav is that the page stays where the reader put it.
            const x = link.offsetLeft - (rail.clientWidth - link.offsetWidth) / 2;
            rail.scrollTo({ left: Math.max(0, x), behavior: reduced ? 'auto' : 'smooth' });
          });
        },
        // Band across the middle of the viewport: whatever is there is what you
        // are reading. Without it, the tallest section would always win.
        { rootMargin: '-45% 0px -50% 0px' }
      );
      targets.forEach((t) => spy.observe(t));
    }
  }

  /* --- collapse a review section Judge.me never filled ----------------- */
  document.querySelectorAll('[data-kc-reviews]').forEach((section) => {
    if (!section.querySelector('.jdgm-widget')) return;

    // Deliberately NOT hidden up front. Judge.me renders client-side and will
    // not paint into a display:none container, so hiding first and waiting for
    // content to appear is a deadlock: the widget stays empty because it is
    // hidden, and stays hidden because it is empty. Leave it visible, and
    // collapse only if nothing ever arrived.
    //
    // Re-queried each time because Judge.me replaces the node rather than
    // filling the one Liquid rendered.
    const filled = () => {
      const w = section.querySelector('.jdgm-widget');
      return !!w && (w.children.length > 0 || w.textContent.trim() !== '');
    };

    const navLink = document.querySelector('.kc-anchors a[href="#' + section.id + '"]');
    const placeholder = navLink && document.createComment('kc-reviews-link');

    setTimeout(() => {
      if (filled()) return;
      section.hidden = true;
      if (navLink && navLink.parentNode) navLink.replaceWith(placeholder);
    }, 6000);
  });

  /* --- carousel: arrow keys step one cell, arrow buttons most of a view - */
  const initRail = (rail) => {
    const step = (dir, page) => {
      const cell = rail.querySelector('.kc-vid__cell');
      if (!cell) return false;
      const w = cell.offsetWidth + 16;
      rail.scrollBy({
        left: (page ? Math.max(w, rail.clientWidth - w) : w) * dir,
        behavior: reduced ? 'auto' : 'smooth',
      });
      return true;
    };
    rail.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
      if (step(e.key === 'ArrowRight' ? 1 : -1)) e.preventDefault();
    });

    // Same idea as the jump-nav arrows: built here so a no-JS page has no
    // dead buttons, hidden at the edge they point past (classes on the box).
    const box = rail.parentElement;
    ['prev', 'next'].forEach((dir) => {
      const el = document.createElement('button');
      el.type = 'button';
      el.className = 'kc-vid__arrow kc-vid__arrow--' + dir;
      el.setAttribute('aria-label', dir === 'next' ? 'More videos' : 'Previous videos');
      el.innerHTML =
        '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" ' +
        'stroke-width="2.2" stroke-linecap="square" aria-hidden="true"><path d="' +
        (dir === 'next' ? 'M9 5l7 7-7 7' : 'M15 5l-7 7 7 7') +
        '"/></svg>';
      el.addEventListener('click', () => step(dir === 'next' ? 1 : -1, true));
      box.append(el);
    });
    const edges = () => {
      const max = rail.scrollWidth - rail.clientWidth;
      box.classList.toggle('at-start', rail.scrollLeft <= 2);
      box.classList.toggle('at-end', rail.scrollLeft >= max - 2);
    };
    edges();
    rail.addEventListener('scroll', edges, { passive: true });
    addEventListener('resize', edges);
  };
  document.querySelectorAll('[data-kc-rail]').forEach(initRail);

  /* --- theme editor: a re-rendered section arrives as new elements ----- */
  // Without this, editing a section in the customizer leaves its videos
  // paused, its rail without arrows and its .kc-reveal heading invisible.
  document.addEventListener('shopify:section:load', (e) => {
    watchVideos(e.target);
    e.target.querySelectorAll('[data-kc-rail]').forEach(initRail);
    e.target.querySelectorAll('.kc-reveal').forEach((el) => el.classList.add('is-in'));
  });
})();
